import { getClientSecurityContext } from './security';
import { supabase } from './supabase';
import type { ManagedRoleCode } from './roles';

export type PanelProfile = {
  id: string;
  full_name: string | null;
  email: string | null;
  role_code: ManagedRoleCode | string;
  is_active: boolean;
  can_access_admin_panel: boolean;
  can_manage_users: boolean;
  can_manage_roles: boolean;
  can_manage_content: boolean;
  can_manage_media: boolean;
  can_manage_pages: boolean;
  can_use_infaz_calculator: boolean;
  can_use_qr_creator: boolean;
  can_use_client_tracking: boolean;
  notes: string | null;
  phone_number?: string | null;
  birth_date?: string | null;
  specialty?: string | null;
  office_address?: string | null;
  last_seen?: string | null;
  created_at?: string;
  updated_at?: string;
};

export type CreateManagedUserPayload = {
  email: string;
  password: string;
  full_name?: string;
  notes?: string;
  profile_photo_url?: string;
  phone_number?: string;
  birth_date?: string;
  role_code?: string;
};

export type UpdateManagedProfilePayload = {
  profileId: string;
  updates: Partial<PanelProfile> & {
    profile_photo_url?: string | null;
    can_view_logs?: boolean;
    can_view_traffic?: boolean;
    phone_number?: string | null;
    birth_date?: string | null;
  };
};

export type AuditLogRow = {
  id: string;
  action_code: string;
  target_table: string | null;
  target_id: string | null;
  actor_email: string | null;
  detail: Record<string, unknown> | null;
  ip_address?: string | null;
  user_agent?: string | null;
  country_code?: string | null;
  browser_name?: string | null;
  browser_version?: string | null;
  os_name?: string | null;
  device_type?: string | null;
  request_path?: string | null;
  created_at?: string;
};

type FunctionErrorContext = {
  json?: () => Promise<{ error?: string; message?: string }>;
  text?: () => Promise<string>;
  status?: number;
};

type InvokeLikeError = Error & {
  context?: FunctionErrorContext;
};

async function extractFunctionErrorMessage(
  error: InvokeLikeError | Error,
  fallback: string
) {
  let detailedMessage = error.message || fallback;

  const fnError = error as InvokeLikeError;

  try {
    if (fnError.context?.json) {
      const body = await fnError.context.json();
      if (body?.error) detailedMessage = body.error;
      else if (body?.message) detailedMessage = body.message;
    } else if (fnError.context?.text) {
      const raw = await fnError.context.text();
      if (raw) detailedMessage = raw;
    }
  } catch {
    // ignore
  }

  if (
    detailedMessage.includes('non-2xx status code') ||
    detailedMessage.includes('FunctionsHttpError')
  ) {
    const status = fnError.context?.status;

    if (status === 401) {
      return 'Yetkisiz istek. Oturum bulunamadı ya da token function tarafında doğrulanamadı.';
    }
    if (status === 403) {
      return 'Bu işlem için yetkin bulunmuyor.';
    }
    if (status === 404) {
      return 'Edge Function bulunamadı. Deploy edilmemiş olabilir.';
    }
    if (status === 500) {
      return 'Sunucu tarafında hata oluştu. Edge Function loglarını kontrol et.';
    }

    return fallback;
  }

  return detailedMessage;
}

export async function getAllProfiles() {
  return await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: true });
}

export async function getAuditLogs(limit = 100) {
  return await supabase
    .from('audit_logs')
    .select('*')
    .order('created_at', { ascending: false })
    .limit(limit);
}

export async function getAuditLogsByAction(actionCode: string, limit = 100) {
  return await supabase
    .from('audit_logs')
    .select('*')
    .eq('action_code', actionCode)
    .order('created_at', { ascending: false })
    .limit(limit);
}

export async function insertAuditLog(payload: {
  action_code: string;
  target_table?: string;
  target_id?: string;
  detail?: Record<string, unknown>;
  actor_email?: string;
}) {
  const clientContext = getClientSecurityContext();

  return await supabase.rpc('write_audit_log', {
    p_action_code: payload.action_code,
    p_target_table: payload.target_table ?? null,
    p_target_id: payload.target_id ?? null,
    p_detail: {
      ...(payload.detail ?? {}),
      client_context: {
        timezone: clientContext.timezone,
        language: clientContext.language,
      },
    },
    p_actor_email: payload.actor_email ?? null,
    p_user_agent:
      clientContext.userAgent,
    p_browser_name: clientContext.browserName,
    p_browser_version: clientContext.browserVersion,
    p_os_name: clientContext.osName,
    p_device_type: clientContext.deviceType,
    p_request_path: clientContext.requestPath,
  });
}

async function getAccessTokenOrError() {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession();

  if (sessionError) {
    return {
      token: null,
      error: new Error(`Oturum alınamadı: ${sessionError.message}`),
    };
  }

  if (!session?.access_token) {
    return {
      token: null,
      error: new Error('Geçerli oturum bulunamadı. Lütfen yeniden giriş yap.'),
    };
  }

  return {
    token: session.access_token,
    error: null,
  };
}

async function invokeWithAuth<T>(
  functionName: string,
  body: Record<string, unknown>,
  fallbackError: string
): Promise<{ data: T | null; error: Error | null }> {
  const authState = await getAccessTokenOrError();

  if (authState.error || !authState.token) {
    return {
      data: null,
      error: authState.error || new Error('Geçerli oturum bulunamadı.'),
    };
  }

  const result = await supabase.functions.invoke(functionName, {
    body,
  });

  if (result.error) {
    const detailedMessage = await extractFunctionErrorMessage(
      result.error as InvokeLikeError,
      fallbackError
    );

    return {
      data: null,
      error: new Error(detailedMessage),
    };
  }

  return {
    data: (result.data as T) ?? null,
    error: null,
  };
}

export async function createManagedUser(payload: CreateManagedUserPayload) {
  return await invokeWithAuth<{ ok?: boolean; user?: unknown; profile?: unknown }>(
    'create-admin-user',
    {
      email: payload.email,
      password: payload.password,
      full_name: payload.full_name ?? null,
      notes: payload.notes ?? null,
      profile_photo_url: payload.profile_photo_url ?? null,
      phone_number: payload.phone_number ?? null,
      birth_date: payload.birth_date ?? null,
      role_code: payload.role_code ?? 'viewer',
    },
    'Yeni kullanıcı oluşturulamadı.'
  );
}

export async function updateManagedProfile(
  payload: UpdateManagedProfilePayload
) {
  return await invokeWithAuth<{ ok?: boolean; profile?: unknown }>(
    'update-profile-permissions',
    {
      profileId: payload.profileId,
      updates: payload.updates,
    },
    'Yetki güncelleme işlemi başarısız oldu.'
  );
}

export async function deleteManagedUser(targetUserId: string) {
  return await invokeWithAuth<{ ok?: boolean; message?: string }>(
    'delete-user',
    { userId: targetUserId },
    'Kullanıcı silinemedi.'
  );
}
